import { isInboxHelpdesk } from 'conversations-internal-schema/inbox/operators/inboxGetters';
import { isValid } from 'routing-ui/routing-rules/operators/isValid';
import InboxUsersRoutingRules from 'routing-ui/routing-rules/records/InboxUsersRoutingRules';
import { useMoveAssignAndPublish } from 'conversations-thread-data/move-to-inbox/public/hooks';
import { useMemo, useState } from 'react';
import isEmpty from 'transmute/isEmpty';
import setIn from 'transmute/setIn';
import { KEEP_ASSIGNEE } from 'routing-ui/routing-rules/constants/RoutingTypes';
import { usePipelineInformation } from './usePipelineInformation';
import { useUpdateCrmObjectPropertiesMutation } from 'conversations-thread-data/tickets/public/hooks';
import { getChannelRoutingDistribution } from 'routing-ui/routing-rules/utils/distribution';
const checkIsRegularRoutingRule = routingRule => routingRule !== KEEP_ASSIGNEE;
export const useConversationHandoffForm = ({
  associatedTicketIds,
  genericChannelId,
  options,
  threadId
}) => {
  const [inbox, setInbox] = useState();
  const [routingRuleDefinition, setRoutingRuleDefinition] = useState(() => new InboxUsersRoutingRules({
    rotationMethod: getChannelRoutingDistribution(genericChannelId)
  }));
  const [comment, setComment] = useState('');
  const [subject, setSubject] = useState('');
  const {
    onError,
    onCompleted
  } = options;
  const ticketId = associatedTicketIds.get(0);
  const {
    pipelineInformation,
    setPipelineInformation
  } = usePipelineInformation(ticketId);
  const updatedProperties = Object.keys(pipelineInformation).map(propertyName => ({
    name: propertyName,
    value: pipelineInformation[propertyName] || undefined
  }));
  const {
    updateCrmObjectProperties: updateTicketProperties
  } = useUpdateCrmObjectPropertiesMutation({
    objectType: 'TICKET',
    objectId: ticketId
  });
  const inboxId = inbox && inbox.id;
  const inboxName = inbox && inbox.name;
  const routingRule = routingRuleDefinition !== KEEP_ASSIGNEE ? routingRuleDefinition : null;
  const inboxIsValid = !isEmpty(inbox);
  const routingRulesDefinitionIsValid = routingRuleDefinition === KEEP_ASSIGNEE || isValid(routingRuleDefinition);
  const isToHelpdesk = inbox ? isInboxHelpdesk(inbox) : false;
  const shouldCreateNewTicket = isToHelpdesk && associatedTicketIds.isEmpty();
  const subjectIsValid = !shouldCreateNewTicket || subject.length > 0;
  const valid = inboxIsValid && routingRulesDefinitionIsValid && subjectIsValid;
  const {
    moveAssignAndPublish,
    moveAssignAndPublishLoading
  } = useMoveAssignAndPublish({
    variables: {
      routingRule,
      threadId,
      subject,
      comment,
      inboxId,
      inboxName,
      pipelineInformation
    },
    onError,
    onCompleted: () => {
      if (!associatedTicketIds.isEmpty() && isToHelpdesk) {
        updateTicketProperties({
          onSuccess: () => {},
          properties: updatedProperties
        }).then(onCompleted).catch(onError);
      } else {
        onCompleted();
      }
    }
  });
  const selectedInboxId = useMemo(() => {
    if (!inbox) {
      return undefined;
    } else {
      const id = inbox.id;
      return id === null ? undefined : id;
    }
  }, [inbox]);
  return {
    isLoading: moveAssignAndPublishLoading,
    isValid: valid,
    setFieldValue: {
      setComment,
      setInbox,
      setRoutingRuleDefinition: newRoutingRule => {
        let update = newRoutingRule;
        if (checkIsRegularRoutingRule(newRoutingRule)) {
          update = setIn(['rotationMethod'], getChannelRoutingDistribution(genericChannelId), newRoutingRule);
        }
        setRoutingRuleDefinition(update);
      },
      setSubject,
      setPipelineInformation
    },
    submitForm: moveAssignAndPublish,
    comment,
    subject,
    pipelineInformation,
    selectedInboxId,
    routingRuleDefinition,
    isToHelpdesk,
    shouldCreateNewTicket
  };
};